import { composeWithDevTools } from '@redux-devtools/extension';
import { applyMiddleware, createStore } from 'redux';
import type { Action, Middleware, Store } from 'redux';
import { loggerMiddleware } from 'common/middleware/logger';

// redux's `Reducer<S, A>` also fixes a preloaded-state shape, and
// `combineReducers` derives that shape as `never` for reducers typed against an
// action union rather than any action, so a combined reducer does not satisfy
// `Reducer<S, A>`. This is the same function without that shape.
export type RootReducer<S, A extends Action> = (
  state: S | undefined,
  action: A
) => S;

/**
 * Configures a redux store. For development, adds action logging, dev tools and a hook to bolt on
 * hot reload functionality.
 */
export default function configureStore<S, A extends Action>(options: {
  rootReducer: RootReducer<S, A>;
  middlewares?: Array<Middleware<{}, S>>;
  hookToStore?: (store: Store<S, A>) => void;
  preloadedState?: S;
}): Store<S, A> {
  const middlewares = options.middlewares || [];
  if (__DEV__) {
    middlewares.push(loggerMiddleware);
  }

  const storeEnhancer = composeWithDevTools(applyMiddleware(...middlewares));
  let store: Store<S, A>;

  if (options.preloadedState) {
    store = createStore(
      options.rootReducer,
      options.preloadedState,
      storeEnhancer
    );
  } else {
    store = createStore(options.rootReducer, storeEnhancer);
  }

  if (options.hookToStore) {
    options.hookToStore(store);
  }

  return store;
}
